import { useEffect, useState } from 'react';
import {
  Badge, Button, Card, Table, TableBody, TableCell, TableHead,
  TableHeaderCell, TableRow, Text, Title,
} from '@tremor/react';
import { downloadInvoiceFile } from '../api/invoiceFile';
import {
  parseCFDI, prettyXml, highlightXml, tipoLabel, tipoColor,
  formaPagoLabel, metodoPagoLabel, usoCFDILabel,
  type CFDISummary,
} from '../lib/cfdi';
import { formatMxn } from '../lib/format';
import { t } from '../lib/i18n';
import IconButton from './IconButton';
import { CloseIcon } from './icons';
import Modal from './Modal';

interface Props {
  sid: number;
  tnum: number;
  /** Helps the server skip its YY/MM directory scan — same param the
   *  download button passes. */
  idate?: string;
  onClose: () => void;
}

/**
 * Color-coded CFDI 4.0 viewer. Fetches the same XML the download button
 * does, parses it client-side, and presents the high-value fields
 * (totals, emisor/receptor, conceptos, sello fiscal, related CFDIs) in
 * sections an accountant can read without opening Excel. A collapsible
 * "Ver XML crudo" section at the bottom shows the pretty-printed source
 * with light syntax coloring for the technical operator.
 */
export default function CFDIViewer({ sid, tnum, idate, onClose }: Props) {
  const [xml, setXml]   = useState<string | null>(null);
  const [err, setErr]   = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [rawOpen, setRawOpen] = useState(false);

  // Fetch on mount. Reuses the same /api/invoice/xml endpoint as the
  // download path; we just keep the body in memory + parse instead of
  // streaming to a Blob save dialog.
  useEffect(() => {
    let alive = true;
    setErr(null);
    const params = new URLSearchParams({ sid: String(sid), tnum: String(tnum) });
    if (idate) params.set('date', idate);
    fetch('/api/invoice/xml?' + params.toString(), { credentials: 'same-origin' })
      .then(async (res) => {
        if (res.status === 404) throw new Error(t.ticket.download.missing);
        if (res.status === 503) throw new Error(t.ticket.download.notConfigured);
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then((body) => { if (alive) setXml(body); })
      .catch((e: Error) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [sid, tnum, idate]);

  const cfdi: CFDISummary | null = xml ? parseCFDI(xml) : null;

  async function downloadXml() {
    setBusy(true);
    try {
      await downloadInvoiceFile('xml', sid, tnum, idate);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} ariaLabel={t.cfdi.title} panelClassName="max-w-5xl p-6">
      <div className="flex items-start gap-3 mb-3">
        <Title>{t.cfdi.title}</Title>
        {cfdi && cfdi.tipoDeComprobante && (
          <Badge color={tipoColor(cfdi.tipoDeComprobante)}>
            {tipoLabel(cfdi.tipoDeComprobante)}
          </Badge>
        )}
        {cfdi && cfdi.uuid && <Badge color="emerald">{t.cfdi.stamped}</Badge>}
        {cfdi && !cfdi.uuid && xml && <Badge color="amber">{t.cfdi.unstamped}</Badge>}
        <div className="ml-auto flex items-center gap-2">
          <Button size="xs" variant="secondary" loading={busy} onClick={downloadXml}>
            {t.ticket.download.xml}
          </Button>
          <IconButton label={t.common.close} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </div>

      {err && (
        <Card>
          <Text className="text-red-700 dark:text-red-400">{err}</Text>
        </Card>
      )}
      {!xml && !err && <Text>{t.common.loading}</Text>}

      {cfdi && (
        <div className="space-y-4">
          {/* Header summary */}
          <Card decoration="top" decorationColor={tipoColor(cfdi.tipoDeComprobante)}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <KV label={t.cfdi.serieFolio} value={`${cfdi.serie || '—'} - ${cfdi.folio || '—'}`} />
              <KV label={t.cfdi.fecha}     value={fmtIsoLocal(cfdi.fecha)} />
              <KV label={t.cfdi.moneda}    value={`${cfdi.moneda || '—'}${cfdi.tipoCambio && cfdi.tipoCambio !== '1' ? ' @ ' + cfdi.tipoCambio : ''}`} />
              <KV
                label={t.cfdi.total}
                value={<span className="font-semibold text-lg">{formatMxn(cfdi.total)}</span>}
              />
              <KV label={t.cfdi.formaPago}  value={formaPagoLabel(cfdi.formaPago)} />
              <KV label={t.cfdi.metodoPago} value={metodoPagoLabel(cfdi.metodoPago)} />
              <KV label={t.cfdi.lugarExpedicion} value={cfdi.lugarExpedicion || '—'} />
              <KV label={t.cfdi.subtotal}   value={formatMxn(cfdi.subTotal)} />
            </div>
          </Card>

          {/* Emisor / Receptor side-by-side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card decoration="left" decorationColor="indigo">
              <Text className="text-tremor-content text-xs uppercase tracking-wide">{t.cfdi.emisor}</Text>
              <Title className="mt-1 text-base">{cfdi.emisor.nombre || '—'}</Title>
              <div className="mt-2 text-sm space-y-1">
                <KV label={t.cfdi.rfc}            value={<span className="font-mono">{cfdi.emisor.rfc || '—'}</span>} />
                <KV label={t.cfdi.regimenFiscal}  value={cfdi.emisor.regimenFiscal || '—'} />
              </div>
            </Card>
            <Card decoration="left" decorationColor="sky">
              <Text className="text-tremor-content text-xs uppercase tracking-wide">{t.cfdi.receptor}</Text>
              <Title className="mt-1 text-base">{cfdi.receptor.nombre || '—'}</Title>
              <div className="mt-2 text-sm space-y-1">
                <KV label={t.cfdi.rfc}                value={<span className="font-mono">{cfdi.receptor.rfc || '—'}</span>} />
                <KV label={t.cfdi.usoCFDI}            value={usoCFDILabel(cfdi.receptor.usoCFDI)} />
                <KV label={t.cfdi.regimenFiscalRec}   value={cfdi.receptor.regimenFiscalReceptor || '—'} />
                <KV label={t.cfdi.domicilioFiscalRec} value={cfdi.receptor.domicilioFiscalReceptor || '—'} />
              </div>
            </Card>
          </div>

          {/* Conceptos */}
          <Card>
            <div className="flex items-baseline gap-2">
              <Title className="text-base">{t.cfdi.conceptos}</Title>
              <Text className="text-tremor-content text-sm">({cfdi.conceptos.length})</Text>
            </div>
            {cfdi.conceptos.length === 0 ? (
              <Text className="mt-2">{t.cfdi.noConceptos}</Text>
            ) : (
              <div className="mt-2 overflow-x-auto">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>{t.cfdi.col.identificador}</TableHeaderCell>
                      <TableHeaderCell>{t.cfdi.col.descripcion}</TableHeaderCell>
                      <TableHeaderCell className="text-right">{t.cfdi.col.cantidad}</TableHeaderCell>
                      <TableHeaderCell>{t.cfdi.col.unidad}</TableHeaderCell>
                      <TableHeaderCell className="text-right">{t.cfdi.col.valorUnitario}</TableHeaderCell>
                      <TableHeaderCell className="text-right">{t.cfdi.col.importe}</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cfdi.conceptos.map((c, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-mono text-xs">{c.noIdentificacion || '—'}</TableCell>
                        <TableCell className="max-w-md whitespace-normal">{c.descripcion}</TableCell>
                        <TableCell className="text-right tabular-nums">{c.cantidad}</TableCell>
                        <TableCell>{c.unidad}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatMxn(c.valorUnitario)}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">{formatMxn(c.importe)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            <div className="mt-3 flex gap-6 justify-end text-sm">
              <KV label={t.cfdi.impuestosTrasladados} value={formatMxn(cfdi.totalImpuestosTrasladados)} />
              <KV label={t.cfdi.impuestosRetenidos}  value={formatMxn(cfdi.totalImpuestosRetenidos)} />
              <KV label={t.cfdi.total} value={<span className="font-semibold">{formatMxn(cfdi.total)}</span>} />
            </div>
          </Card>

          {/* Sello fiscal — only when stamped */}
          {cfdi.uuid && (
            <Card decoration="top" decorationColor="emerald">
              <Text className="text-tremor-content text-xs uppercase tracking-wide">{t.cfdi.selloFiscal}</Text>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <KV label="UUID"                   value={<span className="font-mono break-all">{cfdi.uuid}</span>} />
                <KV label={t.cfdi.fechaTimbrado}   value={fmtIsoLocal(cfdi.fechaTimbrado)} />
                <KV label={t.cfdi.noCertificadoSat} value={<span className="font-mono text-xs">{cfdi.noCertificadoSAT || '—'}</span>} />
                <KV label={t.cfdi.noCertificado}   value={<span className="font-mono text-xs">{cfdi.noCertificado || '—'}</span>} />
              </div>
            </Card>
          )}

          {/* CfdiRelacionados */}
          {cfdi.relacionados.length > 0 && (
            <Card>
              <Title className="text-base">{t.cfdi.relacionados}</Title>
              <Table className="mt-2">
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>{t.cfdi.col.tipoRelacion}</TableHeaderCell>
                    <TableHeaderCell>UUID</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cfdi.relacionados.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell>{r.tipoRelacion || '—'}</TableCell>
                      <TableCell className="font-mono text-xs break-all">{r.uuid}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}

          {/* Raw XML — collapsible accordion. Light syntax coloring via
              highlightXml so the dev/technical operator can verify the
              stamping without leaving the page. */}
          <Card className="p-0 overflow-hidden">
            <button
              type="button"
              className="w-full px-5 py-3 flex items-center justify-between hover:bg-tremor-background-muted"
              onClick={() => setRawOpen((x) => !x)}
            >
              <Text className="text-tremor-content-strong font-medium">
                {rawOpen ? '▾ ' : '▸ '}{t.cfdi.rawXml}
              </Text>
              <Text className="text-tremor-content text-xs">{xml?.length ?? 0} chars</Text>
            </button>
            {rawOpen && xml && (
              <pre
                className="px-5 pb-4 text-xs overflow-x-auto whitespace-pre font-mono leading-relaxed bg-tremor-background-muted"
                style={{ maxHeight: '50vh', overflowY: 'auto' }}
                dangerouslySetInnerHTML={{ __html: highlightXml(prettyXml(xml)) }}
              />
            )}
          </Card>
        </div>
      )}
    </Modal>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-tremor-content text-xs">{label}</div>
      <div className="text-tremor-content-strong">{value}</div>
    </div>
  );
}

// CFDI Fecha + FechaTimbrado are ISO 8601 without explicit timezone (SAT
// convention: zone of the issuing emisor). Render in es-MX, drop the T.
function fmtIsoLocal(iso: string): string {
  if (!iso) return '—';
  // Just swap the literal T for a space so the value reads naturally;
  // browser timezone games would distort the SAT-recorded local time.
  return iso.replace('T', ' ');
}
